<template>
	<div
		class="locations-page"
		:class="{
			'locations-page--geo-loading': loading,
		}"
	>
		<div class="divider">
			<div class="divider__left">
					<div class="location-box insicht insicht--fade-up insicht--fast">
							<div class="location-box__wrapper">
									<h2 class="location-box__title" v-html="$t('locations.title')"/>
									<div class="location-box__content">
											<div class="location-box__links">
													<Button
														class="location-box__link"
														icon="geo-location"
														:text="$t('locations.geoButton')"
														type="button"
														level="tertiary"
														arrow
														v-if="geolocationAvailable"
														@click="geolocationRequest"
													/>
											</div>
									</div>
							</div>
					</div>
			</div>
			<div class="divider__right divider__right--overlap">
        <!-- <div class="location-map"></div> -->
				<div
					class="location-grid"
					:class="{
						'location-grid--geo-loading': loading,
					}"
				>
					<div
						class="location-grid__item insicht insicht--fade-in"
						v-for="location in processedLocations"
						:key="location.id"
					>
						<div class="location-grid__item-wrapper">
								<a
									class="location-grid__item-image"
									:href="location.url"
								>
									<img
										class="location-grid__item-image-element"
										:srcset="location.image.srcset"
										:src="location.image.src"
										:alt="location.title"
										data-object-fit
										loading="lazy"
									>
								</a>
								<a
									class="location-grid__item-title"
									:href="location.url"
									v-html="location.title"
								/>
								<span
									v-if="showDistance"
									class="location-grid__item-distance"
									v-html="formatDistance(location.distance)"
								/>
								<ul class="location-grid__item-icons">
										<li
											class="location-grid__item-icon"
											v-if="location.telephone"
										>
												<a
													class="location-grid__item-icon-link"
													:href="telUrl(location.telephone)"
												>
														<SVGPhone/>
												</a>
										</li>
										<li
											class="location-grid__item-icon"
											v-if="location.googleMapsUrl"
										>
												<a
													class="location-grid__item-icon-link"
													:href="location.googleMapsUrl"
													target="_blank"
												>
														<SVGLocation/>
												</a>
										</li>
								</ul>
								<div class="location-grid__item-links">
									<!-- TODO: this (home) url should be read from the config -->
									<Button
										v-if="showOrder(location)"
										class="location-grid__item-link"
										:text="$t('locations.orderButton')"
										type="button"
										level="tertiary"
										arrow
										@click="onOrder(location.code)"
									/>
									<Button
										v-if="showReservation(location)"
										class="location-grid__item-link"
										:text="$t('locations.reserveButton')"
										type="button"
										level="tertiary"
										arrow
										@click.prevent="onReservation(location.code)"
									/>
								</div>
								<div class="location-grid__item-openings">
									<div
										class="location-grid__item-openings-special"
										v-if="location.specialOpeningHours"
									>
										<div
											class="location-grid__item-openings-special-entry"
											v-for="(opening, index) in location.specialOpeningHours"
											:key="`special-opening-${index}`"
											v-if="opening.message"
											v-html="opening.message"
										/>
									</div>
									<template v-if="location.openingHours.length">
										<table class="location-grid__item-openings-table">
												<tr
													class="location-grid__item-openings-table-row"
													v-for="(opening, index) in location.openingHours"
													:key="`${index}-${opening.day}`"
												>
														<td
															class="location-grid__item-openings-table-cell"
															v-html="opening.day"
														/>
														<td
															class="location-grid__item-openings-table-cell"
															v-html="opening.times"
														/>
												</tr>
										</table>
										<div
											class="location-grid__item-openings-message"
											v-html="location.openTimesHtml"
										/>
									</template>
									<div
										v-else
										class="location-grid__item-openings-message"
										v-html="$t('locations.closedTitle')"
									/>
								</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import { mapGetters } from 'vuex';
import htmlComponent from 'htmlcomponent'
import Button from '@/components/Button';

export default {
	components: {
		Button,
	},
	data() {
		return {
			processedLocations: this.$root.options.locations || [],
			geolocationAvailable: 'geolocation' in navigator,
			showDistance: false,
		};
	},
	computed: {
		...mapGetters('loader', [
			'isLoading',
		]),
		loading: {
			set(value) {
				this.$store.dispatch('loader/setLoading', value);
				this.$emit('loading', value);
			},
			get() {
				return this.isLoading;
			}
		},
	},
	methods: {
		telUrl(tel) {
			return `tel:${tel.replace(/\s/ig, '')}`;
		},
		onOrder(code) {
			htmlComponent.send('overlay:open', {type: 'overlay-orderbox', locationCode: code});
		},
		onIndining(code) {
			if (this.hasFeature('FEATURE_RESERVATION')) {
				this.$store.dispatch('redirect', { name: 'indining-restaurant', params: { code} })
			}
		},
		onReservation(code) {
			if (this.hasFeature('FEATURE_RESERVATION')) {
				this.$store.dispatch('redirect', { name: 'reservation-restaurant', params: { code} })
			}
		},
		showReservation(location) {
			return this.hasFeature('FEATURE_RESERVATION') && location.hasReservation && !!location.foratableRestaurantHash;
		},
    showOrder(location) {
      return (this.hasFeature('FEATURE_ORDERBOX_DELIVERY') || this.hasFeature('FEATURE_ORDERBOX_TAKEAWAY')) && location.openingHours?.length
    },
		geolocationRequest() {
			this.loading = true;
			// open chrome with the following command to test this locally (Mac)
			// open -a /Applications/Google\ Chrome.app --args --unsafely-treat-insecure-origin-as-secure="http://fwgcontao.username.local"
			navigator.geolocation.getCurrentPosition(this.geolocationSuccess.bind(this), this.geolocationError.bind(this));
		},
		geolocationSuccess(position) {
			this.calculateDistance(position.coords);
			this.loading = false;
		},
		geolocationError() {
			this.geolocationAvailable = false;
			this.loading = false;
		},
		calculateDistance({latitude, longitude}) {
			this.processedLocations.forEach(location => {

				// calucation algorithm from here
				// https://stackoverflow.com/a/26837460

				const radlat1 = Math.PI * latitude / 180;
				const radlat2 = Math.PI * location.latitude / 180;
				const radlon1 = Math.PI * longitude / 180;
				const radlon2 = Math.PI * location.longitude / 180;
				const theta = longitude - location.longitude;
				const radtheta = Math.PI * theta  /  180;
				let dist = Math.sin(radlat1) * Math.sin(radlat2) + Math.cos(radlat1) * Math.cos(radlat2) * Math.cos(radtheta);
				dist = Math.acos(dist);
				dist = dist * 180 / Math.PI;
				dist = dist * 60 * 1.1515;
				dist = dist * 1.609344;
				location.distance = dist;
			});
			this.showDistance = true;
			this.sortLocations();
		},
		sortLocations() {
			this.processedLocations.sort((a, b) => {
				return a.distance - b.distance;
			});
		},
		formatDistance(distance) {
			let formatted;
			if (distance < 1) {
				formatted = `${Math.max(Math.round(distance * 1000), 1)}m`;
			}
			else {
				formatted = `${Math.round(distance * 10) / 10}km`;
			}
			return this.$t('locations.distance', { distance: formatted });
		}
	},
	created() {
		this.loading = true;
	},
	mounted() {
		this.loading = false;
	}
}
</script>

<style>

</style>
